/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import useControlled from '@atlaskit/ds-lib/use-controlled';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelContext } from '../../panels-renderer/panel-context';
import { PanelRouter } from '../../panels-renderer/panel-router';
import { PanelsRenderer } from '../../panels-renderer/panels-renderer';
import { LayoutWithPanel as ChatLayoutWithPanel } from './layout-with-panel-chat-panel-layout';
import { PanelSlot } from './panel-slot';

const styles = cssMap({
	layout: {
		display: 'grid',
		gridTemplateAreas: `'main'`,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: '1fr',
		height: '100%',
		width: '100%',
		// Panel is only shown as a separate column on large viewports
		'@media (min-width: 90rem)': {
			gridTemplateAreas: `
                "main panel"
           `,
			gridTemplateRows: 'auto',
			gridTemplateColumns: 'minmax(0,1fr) auto',
		},
		overflow: 'auto',
	},
	mainContent: {
		gridArea: 'main',
		overflow: 'auto',
		display: 'flex',
		height: '100%',
		isolation: 'isolate',
	},
	panelUplift: {
		overflow: 'hidden',
	},
});

export type LayoutWithPanelProps = {
	/**
	 * The requested panel width, in pixels.
	 * CSS may constrain the visible width without changing this value.
	 * Existing responsive bounds still apply; custom maximum widths are not supported.
	 *
	 * Providing `panelWidth` and `onPanelWidthChange` makes the layout controlled and allows the
	 * width to be changed programmatically after mounting when
	 * `platform-dst-panel-system-width-persistence` is enabled. For example, pass
	 * `state.panelWidth` and `manager.setPanelWidth` to connect provider actions to the layout.
	 * Otherwise this sets only the initial width; later prop changes are ignored.
	 * Defaults to the width stored in `PanelProvider`, or 365px.
	 */
	panelWidth?: number;
	/**
	 * Called with the requested width after a pointer resize or each keyboard resize step.
	 * Only called when `platform-dst-panel-system-width-persistence` is enabled;
	 * programmatic width changes do not invoke this callback.
	 *
	 * Provide this with `panelWidth` to control the rendered width, including programmatic width
	 * changes. For persistence that only needs the completed width, prefer `onResizeEnd`.
	 */
	onPanelWidthChange?: (panelWidth: number) => void;
	/**
	 * Minimum width for the chat-panel layout. Defaults to 320px.
	 */
	panelMinWidth?: number;
	/**
	 * Optional maximum width for the chat-panel layout.
	 */
	panelMaxWidth?: number;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when a pointer or keyboard resize interaction is complete.
	 * Intended for product-owned analytics and persistence that should only run once per interaction.
	 *
	 * @param finalWidth number representing the new width of the panel in px
	 */
	onResizeEnd?: (finalWidth: number) => void;
};

function LegacyLayoutWithPanel({
	children,
	panelWidth,
	testId,
	onPanelWidthChange,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();
	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	const isPanelWidthControlled =
		fg('platform-dst-panel-system-width-persistence') &&
		panelWidth !== undefined &&
		onPanelWidthChange !== undefined;
	const [initialPanelWidth] = useState(() => panelWidth ?? state.panelWidth);
	const [currentPanelWidth, setCurrentPanelWidth] = useControlled(
		isPanelWidthControlled ? panelWidth : undefined,
		() => initialPanelWidth,
	);
	const panelSlotTestId = testId && `${testId}--panel-slot`;

	const activePanelRef = useRef(state.activePanels[0]);
	const managerRef = useRef(manager);

	// Keep refs up to date on every render so the cleanup always sees the latest values
	activePanelRef.current = state.activePanels[0];
	managerRef.current = manager;

	useEffect(() => {
		manager.setPanelWidth(currentPanelWidth);
	}, [currentPanelWidth, manager]);

	const handlePanelWidthChange = useCallback(
		(finalWidth: number) => {
			setCurrentPanelWidth(finalWidth);
			if (fg('platform-dst-panel-system-width-persistence')) {
				onPanelWidthChange?.(finalWidth);
			}
		},
		[onPanelWidthChange, setCurrentPanelWidth],
	);

	const handleResizeEnd = useCallback(
		(finalWidth: number) => {
			onResizeEnd?.(finalWidth);
		},
		[onResizeEnd],
	);

	useEffect(() => {
		/**
		 * Close the active panel when this component unmounts.
		 * Ensures panels do not persist when navigating to a new page.
		 */
		return () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		};
	}, []);

	/**
	 * The layout position identifies the persistent panel slot, while the panel instance ID
	 * identifies the changing content within that slot. This keeps the slot mounted during a content
	 * replacement so only its content transitions.
	 */
	const renderedPanelSlots = state.activePanels.map((panel, index) => (
		<Fragment key={`panel-slot-${index}`}>
			<PanelSlot
				contentKey={panel.instanceId}
				defaultWidth={initialPanelWidth}
				width={currentPanelWidth}
				onWidthChange={handlePanelWidthChange}
				onResizeEnd={handleResizeEnd}
				testId={panelSlotTestId}
			>
				<PanelContext.Provider value={panel}>
					<PanelRouter />
				</PanelContext.Provider>
			</PanelSlot>
		</Fragment>
	));

	return (
		<Box xcss={cx(styles.layout, isMotionUpliftEnabled && styles.panelUplift)} testId={testId}>
			<Box xcss={styles.mainContent}>{children}</Box>
			{isMotionUpliftEnabled ? (
				<ExitingPersistence>{renderedPanelSlots}</ExitingPersistence>
			) : (
				<PanelsRenderer>
					<PanelRouter
						wrapper={({ children }) => (
							<PanelSlot
								defaultWidth={initialPanelWidth}
								width={currentPanelWidth}
								onWidthChange={handlePanelWidthChange}
								onResizeEnd={handleResizeEnd}
								testId={panelSlotTestId}
							>
								{children}
							</PanelSlot>
						)}
					/>
				</PanelsRenderer>
			)}
		</Box>
	);
}

/**
 * Preserve existing panel behavior until the new layout is enabled.
 */
export function LayoutWithPanel({
	children,
	panelWidth,
	panelMinWidth,
	panelMaxWidth,
	testId,
	onPanelWidthChange,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	return fg('platform-dst-chat-panel-layout') ? (
		<ChatLayoutWithPanel
			panelWidth={panelWidth}
			onPanelWidthChange={onPanelWidthChange}
			panelMinWidth={panelMinWidth}
			panelMaxWidth={panelMaxWidth}
			testId={testId}
			onResizeEnd={onResizeEnd}
		>
			{children}
		</ChatLayoutWithPanel>
	) : (
		<LegacyLayoutWithPanel
			panelWidth={panelWidth}
			testId={testId}
			onPanelWidthChange={onPanelWidthChange}
			onResizeEnd={onResizeEnd}
		>
			{children}
		</LegacyLayoutWithPanel>
	);
}
